import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, ArrowUpRight } from "lucide-react";
import { BOOKS, ARTICLES } from "../data/content";

export const SearchOverlay = ({ open, onClose }) => {
  const [q, setQ] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setTimeout(() => inputRef.current?.focus(), 50);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => {
      window.removeEventListener("keydown", esc);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const needle = q.trim().toLowerCase();
  const books = useMemo(() => {
    if (!needle) return BOOKS.slice(0, 3);
    return BOOKS.filter((b) =>
      [b.title, b.subtitle, b.author, b.blurb].join(" ").toLowerCase().includes(needle)
    );
  }, [needle]);
  const articles = useMemo(() => {
    if (!needle) return ARTICLES.slice(0, 4);
    return ARTICLES.filter((a) =>
      [a.title, a.excerpt, a.category, a.author, (a.tags || []).join(" ")]
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [needle]);

  const nothing = needle && books.length === 0 && articles.length === 0;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="search-overlay"
          className="fixed inset-0 z-[60]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <motion.div
            onClick={onClose}
            className="absolute inset-0 bg-[var(--ink)]/85 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -30, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto mt-16 lg:mt-24 w-full max-w-4xl px-5"
          >
            <div className="bg-[var(--bg)] border border-[var(--border-c)] rounded-sm shadow-2xl overflow-hidden">
              {/* Input row */}
              <div className="flex items-center gap-3 px-5 lg:px-7 py-5 border-b border-[var(--border-c)]">
                <Search className="h-5 w-5 text-[var(--text-2)]" />
                <input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search books, writings, authors…"
                  data-testid="search-input"
                  className="flex-1 bg-transparent font-serif text-xl sm:text-2xl text-[var(--text)] placeholder:text-[var(--text-2)]/50 focus:outline-none"
                />
                <button
                  onClick={onClose}
                  data-testid="search-close-button"
                  aria-label="Close search"
                  className="h-9 w-9 rounded-full border border-[var(--border-c)] text-[var(--text)] grid place-items-center hover:bg-[var(--text)] hover:text-[var(--bg)] transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Results */}
              <div className="max-h-[65vh] overflow-y-auto">
                {nothing ? (
                  <div className="px-7 py-16 text-center">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--text-2)]">
                      No results
                    </div>
                    <h4 className="mt-3 font-serif text-2xl text-[var(--text)]">
                      Nothing found in the archive for “{q}”.
                    </h4>
                    <p className="mt-2 font-sans text-sm text-[var(--text-2)]">
                      Try a shorter query — like “Angkor”, “Groslier”, or “Sappho”.
                    </p>
                  </div>
                ) : (
                  <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-[var(--border-c)]">
                    {/* Books */}
                    <div className="p-5 lg:p-7">
                      <div className="flex items-center justify-between pb-3 border-b border-[var(--border-c)]">
                        <span className="font-mono text-[10px] uppercase tracking-[0.26em] text-[var(--gold)]">
                          Books · {books.length}
                        </span>
                        <Link
                          to="/books"
                          onClick={onClose}
                          className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--text-2)] hover:text-[var(--text)]"
                        >
                          All books →
                        </Link>
                      </div>
                      <ul className="mt-2">
                        {books.map((b) => (
                          <li key={b.id}>
                            <Link
                              to={`/books/${b.id}`}
                              onClick={onClose}
                              data-testid={`search-book-${b.id}`}
                              className="group flex items-start gap-4 py-3"
                            >
                              <div className="shrink-0 h-16 w-12 overflow-hidden rounded-[2px] bg-[var(--surface-2)]">
                                <img
                                  src={b.cover}
                                  alt={b.title}
                                  className="h-full w-full object-cover"
                                />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="font-serif text-base leading-snug text-[var(--text)] group-hover:text-[var(--terracotta)] transition-colors">
                                  {b.title}
                                </div>
                                <div className="font-sans text-xs text-[var(--text-2)] mt-0.5 truncate">
                                  {b.author}
                                </div>
                              </div>
                              <ArrowUpRight className="h-4 w-4 text-[var(--text-2)] opacity-0 group-hover:opacity-100 transition-opacity" />
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Writings */}
                    <div className="p-5 lg:p-7">
                      <div className="flex items-center justify-between pb-3 border-b border-[var(--border-c)]">
                        <span className="font-mono text-[10px] uppercase tracking-[0.26em] text-[var(--gold)]">
                          Writings · {articles.length}
                        </span>
                        <Link
                          to="/writings"
                          onClick={onClose}
                          className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--text-2)] hover:text-[var(--text)]"
                        >
                          All writings →
                        </Link>
                      </div>
                      <ul className="mt-2">
                        {articles.slice(0, 6).map((a) => (
                          <li key={a.id}>
                            <Link
                              to={`/writings/${a.id}`}
                              onClick={onClose}
                              data-testid={`search-article-${a.id}`}
                              className="group block py-3"
                            >
                              <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--terracotta)]">
                                {a.category}
                              </span>
                              <h5 className="mt-1 font-serif text-base leading-snug text-[var(--text)] group-hover:text-[var(--terracotta)] transition-colors">
                                {a.title}
                              </h5>
                              <span className="mt-0.5 block font-sans text-xs text-[var(--text-2)]">
                                {a.date} · {a.readTime}
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer tip */}
              <div className="px-5 lg:px-7 py-3 bg-[var(--surface)] border-t border-[var(--border-c)] flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.24em] text-[var(--text-2)]">
                <span>Press ESC to close</span>
                <span className="hidden sm:inline">
                  Devata &amp; DatAsia · search
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
